import type { HttpTransport } from './httpTransport'
import { HealthShareApiError } from './httpTransport'
import type { operations } from './generated/types'

/**
 * The typed backend client generated (in spirit -- see the file header note below) from
 * apps/reimbursement-tracker-admin/openapi.yaml. One method per `jwt.auth` operation the
 * client actually calls -- `reconcileTodos`, `sync`, and `uploadBlobContent` are real
 * spec entries but are deliberately not here, because docs/plans/native-app-sdk-seams.md
 * Stage 3 keeps them on the pre-existing `BackendClient` seam (`lib/sync/syncActor.ts`,
 * `platform/fileStore.ts`'s upload driver) rather than migrating them onto this one.
 *
 * This file is hand-written, not run through a code generator, but it is checked
 * *against* generated types (`./generated/types.ts`, produced by
 * `pnpm run generate-backend-client`): every method's parameter and return type is
 * derived from `operations[operationId]`, so a spec change that alters a shape breaks
 * `pnpm check-types` here instead of breaking a screen on a device. See
 * docs/plans/native-app-sdk-seams.md, Stage 2 Step 2, "Generate the backend client from
 * the spec" -- the split described there is `HttpTransport` (the injected adapter) plus
 * this file (the operations built over it).
 */

type Json200<K extends keyof operations> = operations[K]['responses'] extends {
  200: { content: { 'application/json': infer R } }
}
  ? R
  : never

// NonNullable first because several operations (e.g. createStripePortalSession)
// have an optional requestBody -- `X | undefined extends {...}` is never true, so
// checking the union directly would always fall through to the `undefined` branch.
type JsonBody<K extends keyof operations> = NonNullable<operations[K]['requestBody']> extends {
  content: { 'application/json': infer B }
}
  ? B
  : undefined

async function request<K extends keyof operations>(
  transport: HttpTransport,
  method: string,
  path: string,
  body?: unknown,
): Promise<Json200<K>> {
  const response = await transport.fetch(path, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    throw await toApiError(response)
  }

  // A 200 with no body would fail this on some endpoints, but every operation this
  // client wraps returns a JSON object on success -- see openapi.yaml.
  return (await response.json()) as Json200<K>
}

async function toApiError(response: Response): Promise<HealthShareApiError> {
  let message = `Request failed: ${response.status}`
  let code: string | undefined
  try {
    const data = (await response.json()) as { error?: unknown; message?: unknown; code?: unknown }
    if (typeof data.error === 'string') {
      message = data.error
    } else if (typeof data.message === 'string') {
      message = data.message
    }
    if (typeof data.code === 'string') {
      code = data.code
    }
  } catch {
    // Non-JSON or empty body -- keep the status-based message.
  }
  return new HealthShareApiError(response.status, code, message)
}

function withQuery(path: string, params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      search.set(key, String(value))
    }
  }
  const qs = search.toString()
  return qs ? `${path}?${qs}` : path
}

export interface BackendApi {
  emailMyself(body: JsonBody<'emailMyself'>): Promise<Json200<'emailMyself'>>
  supportContact(body: JsonBody<'supportContact'>): Promise<Json200<'supportContact'>>
  rereadBlob(membershipId: string, blobKey: string): Promise<Json200<'rereadBlob'>>
  sendItemizedBillNow(body: JsonBody<'sendItemizedBillNow'>): Promise<Json200<'sendItemizedBillNow'>>
  searchPlaces(body: JsonBody<'searchPlaces'>): Promise<Json200<'searchPlaces'>>
  processConversationNow(conversationId: string): Promise<Json200<'processConversationNow'>>
  inviteSpouse(membershipId: string, body: JsonBody<'inviteSpouse'>): Promise<Json200<'inviteSpouse'>>
  getMyOrganization(query?: { scope?: 'membership' | 'all' }): Promise<Json200<'getMyOrganization'>>
  getMyMembershipContext(): Promise<Json200<'getMyMembershipContext'>>
  updateMyProfile(body: JsonBody<'updateMyProfile'>): Promise<Json200<'updateMyProfile'>>
  updateMySettings(body: JsonBody<'updateMySettings'>): Promise<Json200<'updateMySettings'>>
  saveMyMembership(body: JsonBody<'saveMyMembership'>): Promise<Json200<'saveMyMembership'>>
  updateMyMembership(
    membershipId: string,
    body: JsonBody<'updateMyMembership'>,
  ): Promise<Json200<'updateMyMembership'>>
  saveDependent(body: JsonBody<'saveDependent'>): Promise<Json200<'saveDependent'>>
  updateDependent(
    dependentId: string,
    body: JsonBody<'updateDependent'>,
  ): Promise<Json200<'updateDependent'>>
  deleteDependent(dependentId: string): Promise<Json200<'deleteDependent'>>
  registerPushToken(body: JsonBody<'registerPushToken'>): Promise<Json200<'registerPushToken'>>
  submitTextractFeedback(
    body: JsonBody<'submitTextractFeedback'>,
  ): Promise<Json200<'submitTextractFeedback'>>
  createStripeCheckoutSession(body: JsonBody<'createStripeCheckoutSession'>): Promise<Json200<'createStripeCheckoutSession'>>
  createStripeTeamCheckoutSession(body: JsonBody<'createStripeTeamCheckoutSession'>): Promise<Json200<'createStripeTeamCheckoutSession'>>
  createStripePortalSession(body?: JsonBody<'createStripePortalSession'>): Promise<Json200<'createStripePortalSession'>>
  upgradeStripeSubscription(): Promise<Json200<'upgradeStripeSubscription'>>
  createPlaidLinkToken(body: JsonBody<'createPlaidLinkToken'>): Promise<Json200<'createPlaidLinkToken'>>
  exchangePlaidPublicToken(body: JsonBody<'exchangePlaidPublicToken'>): Promise<Json200<'exchangePlaidPublicToken'>>
  exchangePlaidHostedLink(body: JsonBody<'exchangePlaidHostedLink'>): Promise<Json200<'exchangePlaidHostedLink'>>
  listPlaidItems(query: { membership_id: string }): Promise<Json200<'listPlaidItems'>>
  disconnectPlaidItem(id: string): Promise<Json200<'disconnectPlaidItem'>>
  syncPlaidItem(id: string): Promise<Json200<'syncPlaidItem'>>
  reconnectPlaidItem(id: string): Promise<Json200<'reconnectPlaidItem'>>
  linkPlaidDraftToExpense(body: JsonBody<'linkPlaidDraftToExpense'>): Promise<Json200<'linkPlaidDraftToExpense'>>
}

export function createBackendApi(transport: HttpTransport): BackendApi {
  return {
    emailMyself: (body) => request<'emailMyself'>(transport, 'POST', '/email-myself', body),
    supportContact: (body) => request<'supportContact'>(transport, 'POST', '/support/contact', body),
    rereadBlob: (membershipId, blobKey) =>
      request<'rereadBlob'>(
        transport,
        'POST',
        `/memberships/${encodeURIComponent(membershipId)}/blobs/${encodeURIComponent(blobKey)}/reread`,
      ),
    sendItemizedBillNow: (body) =>
      request<'sendItemizedBillNow'>(transport, 'POST', '/itemized-bill/send-now', body),
    searchPlaces: (body) => request<'searchPlaces'>(transport, 'POST', '/places/search', body),
    processConversationNow: (conversationId) =>
      request<'processConversationNow'>(
        transport,
        'POST',
        `/conversations/${encodeURIComponent(conversationId)}/process-now`,
      ),
    inviteSpouse: (membershipId, body) =>
      request<'inviteSpouse'>(
        transport,
        'POST',
        `/memberships/${encodeURIComponent(membershipId)}/invite-spouse`,
        body,
      ),
    getMyOrganization: (query) =>
      request<'getMyOrganization'>(transport, 'GET', withQuery('/organizations/mine', query ?? {})),
    getMyMembershipContext: () => request<'getMyMembershipContext'>(transport, 'GET', '/memberships/mine'),
    updateMyProfile: (body) => request<'updateMyProfile'>(transport, 'PATCH', '/profiles/mine', body),
    updateMySettings: (body) => request<'updateMySettings'>(transport, 'PATCH', '/settings/mine', body),
    saveMyMembership: (body) => request<'saveMyMembership'>(transport, 'POST', '/memberships', body),
    updateMyMembership: (membershipId, body) =>
      request<'updateMyMembership'>(
        transport,
        'PATCH',
        `/memberships/${encodeURIComponent(membershipId)}`,
        body,
      ),
    saveDependent: (body) => request<'saveDependent'>(transport, 'POST', '/dependents', body),
    updateDependent: (dependentId, body) =>
      request<'updateDependent'>(transport, 'PATCH', `/dependents/${encodeURIComponent(dependentId)}`, body),
    deleteDependent: (dependentId) =>
      request<'deleteDependent'>(transport, 'DELETE', `/dependents/${encodeURIComponent(dependentId)}`),
    registerPushToken: (body) => request<'registerPushToken'>(transport, 'POST', '/push-tokens', body),
    submitTextractFeedback: (body) =>
      request<'submitTextractFeedback'>(transport, 'POST', '/textract-feedbacks', body),
    createStripeCheckoutSession: (body) =>
      request<'createStripeCheckoutSession'>(transport, 'POST', '/stripe/create-checkout-session', body),
    createStripeTeamCheckoutSession: (body) =>
      request<'createStripeTeamCheckoutSession'>(transport, 'POST', '/stripe/create-team-checkout-session', body),
    createStripePortalSession: (body) =>
      request<'createStripePortalSession'>(transport, 'POST', '/stripe/create-portal-session', body),
    upgradeStripeSubscription: () =>
      request<'upgradeStripeSubscription'>(transport, 'POST', '/stripe/upgrade-subscription'),
    createPlaidLinkToken: (body) =>
      request<'createPlaidLinkToken'>(transport, 'POST', '/plaid/create-link-token', body),
    exchangePlaidPublicToken: (body) =>
      request<'exchangePlaidPublicToken'>(transport, 'POST', '/plaid/exchange-public-token', body),
    exchangePlaidHostedLink: (body) =>
      request<'exchangePlaidHostedLink'>(transport, 'POST', '/plaid/exchange-hosted-link', body),
    listPlaidItems: (query) => request<'listPlaidItems'>(transport, 'GET', withQuery('/plaid/items', query)),
    disconnectPlaidItem: (id) =>
      request<'disconnectPlaidItem'>(transport, 'POST', `/plaid/items/${encodeURIComponent(id)}/disconnect`),
    syncPlaidItem: (id) =>
      request<'syncPlaidItem'>(transport, 'POST', `/plaid/items/${encodeURIComponent(id)}/sync`),
    reconnectPlaidItem: (id) =>
      request<'reconnectPlaidItem'>(transport, 'POST', `/plaid/items/${encodeURIComponent(id)}/reconnect`),
    linkPlaidDraftToExpense: (body) =>
      request<'linkPlaidDraftToExpense'>(transport, 'POST', '/plaid/expenses/link-draft', body),
  }
}
